import React, { useState, useRef, useEffect } from "react";
import { Typography } from "@mui/material";
import ArrowIcon from "../../../../assets/drop-down.svg";
import {
  StyledDropdownContainer,
  StyledDropdownButton,
  StyledDropdownArrowContainer,
  StyledDropdownMenu,
  StyledDropdownItem,
} from "../styles";

export interface SalesRep {
  id: string;
  name: string;
  email?: string;
}

interface SalesRepDropdownProps {
  selectedSalesRep: string;
  setSelectedSalesRep: (rep: string) => void;
  salesReps: SalesRep[];
  disabled?: boolean;
  label?: string;
  showConditionally?: boolean;
  dashboardType?: string;
}

const SalesRepDropdown: React.FC<SalesRepDropdownProps> = ({
  selectedSalesRep,
  setSelectedSalesRep,
  salesReps = [],
  disabled = false,
  label = "Sales Representative",
  showConditionally = false,
  dashboardType = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const shouldRender = !(
    showConditionally && dashboardType !== "salesrep-analytics"
  );

  const handleToggle = () => {
    if (disabled) return;
    setIsOpen((prev) => !prev);
  };

  const handleSelect = (value: string) => {
    if (disabled) return;
    setSelectedSalesRep(value);
    setIsOpen(false);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const getSelectedLabel = (): string => {
    if (!selectedSalesRep) return "All Sales Reps";

    const rep = salesReps.find(
      (rep) => (rep.email || rep.id) === selectedSalesRep
    );
    return rep ? rep.name : "All Sales Reps";
  };

  if (!shouldRender) {
    return null;
  }

  return (
    <StyledDropdownContainer ref={dropdownRef}>
      <StyledDropdownButton onClick={handleToggle} disabled={disabled}>
        <span>{getSelectedLabel()}</span>
        <StyledDropdownArrowContainer isOpen={isOpen}>
          <img
            src={ArrowIcon}
            alt="dropdown-arrow"
            style={{ width: "10px", height: "10px" }}
          />
        </StyledDropdownArrowContainer>
      </StyledDropdownButton>

      {isOpen && (
        <StyledDropdownMenu
          style={disabled ? { pointerEvents: "none", opacity: 0.5 } : {}}
        >
          <StyledDropdownItem onClick={() => handleSelect("")}>
            <Typography variant="body2">All Sales Reps</Typography>
          </StyledDropdownItem>

          {salesReps.map((rep, index) => (
            <div key={rep.id}>
              {index !== 0 && (
                <hr
                  style={{
                    margin: 0,
                    border: "none",
                    borderTop: "1px solid #e0e0e0",
                  }}
                />
              )}
              <StyledDropdownItem
                onClick={() => handleSelect(rep.email || rep.id)}
              >
                <Typography variant="body2">{rep.name}</Typography>
              </StyledDropdownItem>
            </div>
          ))}
        </StyledDropdownMenu>
      )}
    </StyledDropdownContainer>
  );
};

export default SalesRepDropdown;
